import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const REFRESH_MS = 5 * 60 * 1000;

/**
 * Due-mission count for the "Missionen" badge (spec §8.2).
 *
 * Degrades silently: a 404 (missions route not mounted), a 5xx (learning DB
 * unavailable) or a network failure all resolve to `null`, which the nav
 * renders as "no badge" rather than as an error. A badge is decoration —
 * it must never be able to break the app frame.
 *
 * @returns {number|null} due count, or null when unknown/unavailable.
 */
export function useDueMissions() {
  const { user, token } = useAuth();
  const [count, setCount] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      const authToken = token ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('etch.token') : null);
      const response = await fetch('/api/missions/due?limit=1', {
        signal,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.ok) return null;
      const data = await response.json();
      const due = Number(data?.counts?.due);
      return Number.isFinite(due) ? due : null;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const refresh = async () => {
      const next = await load(controller.signal);
      if (!cancelled) setCount(next);
    };

    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load, user?.id]);

  return count;
}

export default useDueMissions;
